// The review fold — WHY a described thing (a photo · a video · a voice note)
// waits for a human, computed at read under the project's review policy and
// never stored (ADR-068 §2; DOMAIN-RULES §7). ONE pure function both ends run:
// the server prints the reasons on every media row, the Workbench's fixtures
// and the client's optimistic updates compute the same list — so a card, the
// inbox count and the lens can never disagree. Its unit tests are the contract.
// ── the sources ladder — most trusted first (DOMAIN-RULES §7) ────────────────
// A person's pick · a REQUEST (the photo was taken for a ticket or checklist
// step that names the subject — trusted like the person's word) · the COUNT
// (one photo per counted asset) · DERIVATION (a category named on a site with
// exactly one asset of it) · a MACHINE read — the last two are proposals until
// a human confirms them, or the project's trust dial for that source accepts.
export const SUBJECT_SOURCES = ['human', 'request', 'count', 'derived', 'machine'];
/** Sources a person is behind — never a proposal. */
export const TRUSTED_SOURCES = ['human', 'request', 'count'];
/** A subject counts as NAMED when a person is behind it or a decision (a
 *  person's, or the trust dial's) confirmed it. */
export function isConfirmedSubject(s) {
    return TRUSTED_SOURCES.includes(s.source) || s.confirmed_at !== null;
}
/** Subjects still standing — a dismissed proposal is kept and marked, but out. */
export function liveSubjects(subjects) {
    return subjects.filter((s) => s.rejected_at === null);
}
// ── the reasons (the words on the card, in this order) ──────────────────────
export const REVIEW_REASONS = ['conflict', 'proposal', 'undescribed', 'off_site', 'no_position', 'unconfirmed_upload'];
export const DEFAULT_REVIEW_POLICY = { undescribed: true, off_site: false, no_position: false, trust_technicians: true };
/** The reasons a file waits, under a policy. Empty = nothing to review. */
export function reviewReasonsOf(file, policy, opts = {}) {
    if (file.folder !== null || file.reviewed_at !== null)
        return [];
    const live = liveSubjects(file.subjects);
    const named = live.filter(isConfirmedSubject);
    const open = live.filter((s) => !isConfirmedSubject(s));
    const reasons = [];
    // Two sources naming different assets with one still open outranks a plain proposal.
    if (open.length > 0 && new Set(live.map((s) => s.asset_id)).size > 1)
        reasons.push('conflict');
    else if (open.length > 0)
        reasons.push('proposal');
    if (policy.undescribed && open.length === 0 && named.length === 0 && file.topics.length === 0)
        reasons.push('undescribed');
    const field = file.anchor.kind === 'ticket';
    if (policy.off_site && field && file.position !== null && !file.position.match)
        reasons.push('off_site');
    if (policy.no_position && field && file.position === null)
        reasons.push('no_position');
    if (!policy.trust_technicians &&
        open.length === 0 &&
        (named.length > 0 || file.topics.length > 0) &&
        named.every((s) => s.source === 'human') &&
        !(file.uploaded_by && opts.isReviewer?.(file.uploaded_by.id)))
        reasons.push('unconfirmed_upload');
    return reasons;
}
/** The inbox lens: media, not reviewed, with at least one reason. */
export function needsReview(file) {
    return file.folder === null && file.reviewed_at === null && file.review_reasons.length > 0;
}
